import {
  AlertTriangle, BarChart3, Boxes, CalendarRange, ClipboardCheck, ClipboardList, DollarSign, Handshake,
  LayoutGrid, ListChecks, Megaphone, Scale, ShoppingCart, Target, TrendingUp, Warehouse, type LucideIcon,
} from "lucide-react"
import type { Market } from "@/lib/useMarket"

// Market scope of a page: "market" pages need one market picked, "all" pages
// cover every market, "either" pages (Home, Revenue) offer All markets plus
// each one, "none" pages are about the person, not a market.
export type Scope = "market" | "all" | "either" | "none"

export type SidebarPage = { key: string; label: string; icon: LucideIcon; scope: Scope; blurb: string }

export type SidebarGroup = {
  heading: string
  // Planning, Purchase and Push get a large heading with an icon.
  main?: LucideIcon
  pages: SidebarPage[]
}

// One list for every page, whatever market is picked. Home and Revenue are
// "either": the remembered market, or All markets.
export const GROUPS: SidebarGroup[] = [
  {
    heading: "",
    pages: [
      { key: "home", label: "Home", icon: LayoutGrid, scope: "either", blurb: "How the business is doing." },
      { key: "revenue", label: "Revenue", icon: BarChart3, scope: "either", blurb: "What actually sold." },
      { key: "my-work", label: "My Work", icon: ListChecks, scope: "none", blurb: "Your tasks." },
    ],
  },
  {
    heading: "Planning", main: Target,
    pages: [
      { key: "top-down", label: "Top Down", icon: Target, scope: "market", blurb: "Revenue targets split across channels and months." },
      { key: "pricings", label: "Product Pricings", icon: DollarSign, scope: "market", blurb: "Planned prices per product and channel." },
      { key: "bottom-up", label: "Bottom Up", icon: TrendingUp, scope: "market", blurb: "Units per product, by month and by day." },
      { key: "plan-vs-actual", label: "Plan vs Actual", icon: Scale, scope: "market", blurb: "What sold against what was planned." },
      { key: "wholesale-customers", label: "Wholesale Customers", icon: Handshake, scope: "market", blurb: "Wholesale accounts and who looks after them." },
      { key: "forecast-by-account", label: "Forecast by Account", icon: CalendarRange, scope: "market", blurb: "Wholesale units per account and month." },
    ],
  },
  {
    heading: "Purchase", main: ShoppingCart,
    pages: [
      { key: "purchase-plan", label: "Purchase Plan", icon: ClipboardList, scope: "all", blurb: "The list of products being bought, all markets." },
      { key: "needs-buying", label: "Needs Buying", icon: ShoppingCart, scope: "all", blurb: "What to buy and how much, worked out from the plan." },
    ],
  },
  {
    heading: "Push", main: Megaphone,
    pages: [
      { key: "open-issues", label: "Open Issues", icon: AlertTriangle, scope: "market", blurb: "Too much stock or slow sellers that need action." },
      { key: "acknowledged", label: "Acknowledged", icon: ClipboardCheck, scope: "market", blurb: "Issues someone is already working on." },
    ],
  },
  {
    heading: "Inventory",
    pages: [
      { key: "by-channel", label: "By Channel", icon: Boxes, scope: "market", blurb: "Stock per sales channel and when it runs out." },
      { key: "by-warehouse", label: "By Warehouse", icon: Warehouse, scope: "market", blurb: "Stock per warehouse." },
      { key: "by-top-down", label: "By Top Down", icon: Boxes, scope: "market", blurb: "Stock against the Top Down plan." },
    ],
  },
]

export const SETTINGS = ["Org Structure", "Team", "Roles", "Warehouses", "Integrations", "Import"]

// Used when the org has no country nodes (fresh dev DB).
export const FALLBACK_MARKETS: Market[] = [
  { id: "vn", slug: "vietnam", name: "Vietnam", currency: "VND" },
  { id: "us", slug: "us", name: "United States", currency: "USD" },
]
